'use client';

import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme, alpha } from '@mui/material/styles';
import { useTranslations } from 'next-intl';
import { WelcomeSection } from './WelcomeSection';
import { FeatureHighlights } from './FeatureHighlights';
import { HelpChat } from './HelpChat';
import type { LandingLayoutProps } from './types';

/**
 * Landing page layout wrapper that handles responsive value proposition display.
 *
 * Desktop: Two-column split (value props left, creation form right)
 * Mobile: Single column (compact value props above, form below)
 *
 * Supports collapse/expand of value proposition section when user starts creating.
 *
 * Epic 6 Note: This layout is a bridge solution. Epic 6 will implement
 * A/B testing for different layouts including:
 * - Desktop: Split-screen vs single-column vs conversation-first
 * - Mobile: Vertical scroll vs swipeable cards vs minimal header
 */
export function LandingLayout({
  children,
  collapsed = false,
  leftContent,
}: LandingLayoutProps) {
  const t = useTranslations('creator.landing');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Mobile layout: Single column with compact value props
  if (isMobile) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          width: '100%',
        }}
      >
        {/* Collapsible value proposition */}
        <Collapse in={!collapsed} timeout={300}>
          <Box sx={{ mb: 1, bgcolor: alpha(theme.palette.primary.main, 0.08), borderRadius: 2, p: 2 }}>
            <WelcomeSection compact />
          </Box>
        </Collapse>

        {/* Creation form */}
        <Box
          sx={{
            bgcolor: 'background.paper',
            borderRadius: 2,
            p: 2,
          }}
        >
          <Typography
            variant="h5"
            fontWeight={600}
            color="primary.main"
            sx={{ mb: 0.5 }}
          >
            {t('contentSection.title')}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 2 }}
          >
            {t('contentSection.subtitle')}
          </Typography>
          {children}
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 2, display: 'block', textAlign: 'center' }}
          >
            {t('contentSection.helperText')}
          </Typography>
        </Box>

        {/* Left content (sources list) shown below form on mobile */}
        {collapsed && leftContent}

        {/* Help chat at bottom (only when not collapsed) */}
        <Collapse in={!collapsed} timeout={300}>
          <HelpChat />
        </Collapse>
      </Box>
    );
  }

  // Desktop layout: Two-column split
  return (
    <Box
      sx={{
        display: 'flex',
        gap: 6,
        width: '100%',
        minHeight: '70vh',
        alignItems: 'flex-start',
      }}
    >
      {/* Left column: Value proposition OR sources list when collapsed */}
      {collapsed && leftContent ? (
        <Box
          sx={{
            flex: '0 0 380px',
            maxWidth: 380,
            position: 'sticky',
            top: 24,
            maxHeight: 'calc(100vh - 48px)',
            overflowY: 'auto',
          }}
        >
          {leftContent}
        </Box>
      ) : (
        <Collapse
          in={!collapsed}
          orientation="horizontal"
          timeout={300}
          sx={{
            flex: collapsed ? 0 : 1,
            minWidth: collapsed ? 0 : 300,
            maxWidth: collapsed ? 0 : 450,
            transition: 'flex 0.3s ease, min-width 0.3s ease, max-width 0.3s ease',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 3,
              pt: 2,
              position: 'sticky',
              top: 24,
              bgcolor: alpha(theme.palette.primary.main, 0.08),
              borderRadius: 3,
              p: 3,
            }}
          >
            <WelcomeSection />
            <FeatureHighlights variant="full" />
            <HelpChat />
          </Box>
        </Collapse>
      )}

      {/* Right column: Creation form */}
      <Box
        sx={{
          flex: 1,
          maxWidth: collapsed && leftContent ? 600 : collapsed ? 700 : 600,
          mx: collapsed && !leftContent ? 'auto' : 0,
          transition: 'max-width 0.3s ease, margin 0.3s ease',
          bgcolor: 'background.paper',
          borderRadius: 3,
          p: { xs: 2, md: 3 },
          boxShadow: 1,
        }}
      >
        <Typography
          variant="h4"
          fontWeight={600}
          color="primary.main"
          sx={{ mb: 1 }}
        >
          {t('contentSection.title')}
        </Typography>
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{ mb: 3 }}
        >
          {t('contentSection.subtitle')}
        </Typography>
        {children}
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mt: 3, textAlign: 'center' }}
        >
          {t('contentSection.helperText')}
        </Typography>
      </Box>
    </Box>
  );
}
